'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';

import PublicNav from '@/components/PublicNav';
import Footer from '@/components/Footer';

const mobileFeatures = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M6.343 17.657a9 9 0 010-12.728M9.172 15.536a5 5 0 010-7.072" />
      </svg>
    ),
    label: 'Offline-First',
    description: 'Capture photos and data anywhere - no signal needed. Everything syncs the moment you reconnect.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    label: 'One-Click Reports (PDF & Word)',
    description: 'Create a polished, customisable PDF or Word report in a single tap. Hours of work, done instantly.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    label: 'Live Location Sharing',
    description: "See your team\'s real-time positions on the map. Know who\'s where for safety and full transparency.",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    label: 'Nearby POI Alerts',
    description: 'Get notified when you\'re close to a mapped point of interest. Never miss something relevant in the field.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    label: 'Realtime Sync',
    description: 'Every photo added on mobile appears instantly on the web map. Your whole team sees the same picture, live.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
    label: 'Precise Location',
    description: "Pin photos to any exact spot on the map - drag to adjust, not just where you're standing.",
  },
];

const webFeatures = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
    title: 'Collaborative Map View',
    description: 'All photos pinned to a live map. Filter, zoom, and explore contributions from every team member in one shared view.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Team Roles & Access',
    description: 'Invite collaborators with the right level of access - from full contributors to read-only viewers.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    title: 'Organised Collections',
    description: 'Group your mapped photos into collections. Label, filter, and manage everything from a clean web dashboard.',
  },
];

export default function HomePage() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const redirected = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-sm-bg flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Nav */}
      <PublicNav showPricingLink />
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-dark via-primary to-primary-light">
        {/* Decorative blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -left-16 w-72 h-72 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28 flex flex-col lg:flex-row items-center gap-12">
          {/* Left: copy */}
          <div className="flex-1 text-center lg:text-left lg:max-w-[44%]">
            <div className="inline-flex items-center gap-2 bg-white/10 text-blue-100 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-white/20">
              <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
              Live collaboration - web &amp; mobile
            </div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight tracking-tight">
              Map photos together,<br />
              <span className="text-accent">in realtime</span>
            </h1>
            <p className="mt-5 text-blue-100 text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed">
              CoLoco lets anyone - solo or in a team - pin geotagged photos to a shared live map. Capture on mobile, view instantly on the web. No signal? No problem.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              {user ? (
                <Link
                  href={isMobile ? '/get-the-app' : '/collections'}
                  className="bg-accent hover:bg-accent-dark text-white font-bold px-8 py-3.5 rounded-xl transition-colors duration-150 shadow-lg text-base"
                >
                  Go to My Collections
                </Link>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="bg-accent hover:bg-accent-dark text-white font-bold px-8 py-3.5 rounded-xl transition-colors duration-150 shadow-lg text-base"
                  >
                    Get Started Free
                  </Link>
                  <Link
                    href="/get-the-app"
                    className="bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-3.5 rounded-xl transition-colors duration-150 border border-white/20 text-base"
                  >
                    Get the App
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Right: Web + App mockup */}
          <div className="flex-1 w-full max-w-lg lg:max-w-none relative">
            {/* Web dashboard mockup - map + list layout */}
            <div className="bg-white/10 backdrop-blur rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-4 py-2.5 bg-white/5 border-b border-white/10">
                <div className="w-2.5 h-2.5 rounded-full bg-white/25" />
                <div className="w-2.5 h-2.5 rounded-full bg-white/25" />
                <div className="w-2.5 h-2.5 rounded-full bg-white/25" />
                <div className="ml-3 flex-1 h-4 bg-white/10 rounded-md max-w-[180px]" />
              </div>
              
              {/* Content: map left, list right */}
              <div className="flex h-52 sm:h-64">
                {/* Map area */}
                <div className="flex-[3] relative bg-primary-dark/40 overflow-hidden">
                  {/* Grid lines to suggest a map */}
                  <div className="absolute inset-0 opacity-20">
                    {[...Array(6)]?.map((_, i) => (
                      <div key={`h${i}`} className="absolute w-full h-px bg-white/40" style={{ top: `${(i + 1) * 16}%` }} />
                    ))}
                    {[...Array(8)]?.map((_, i) => (
                      <div key={`v${i}`} className="absolute h-full w-px bg-white/40" style={{ left: `${(i + 1) * 12}%` }} />
                    ))}
                  </div>
                  
                  {/* Map pins */}
                  {[
                    { top: '22%', left: '18%', accent: true },
                    { top: '45%', left: '38%', accent: false },
                    { top: '30%', left: '58%', accent: false },
                    { top: '62%', left: '25%', accent: false },
                    { top: '55%', left: '70%', accent: true },
                    { top: '18%', left: '75%', accent: false },
                  ]?.map((pin, i) => (
                    <div
                      key={i}
                      className={`absolute w-4 h-4 rounded-full border-2 border-white shadow-md ${pin?.accent ? 'bg-accent' : 'bg-white/70'}`}
                      style={{ top: pin?.top, left: pin?.left, transform: 'translate(-50%,-50%)' }}
                    />
                  ))}
                  
                  {/* Live location dot */}
                  <div className="absolute" style={{ top: '48%', left: '52%', transform: 'translate(-50%,-50%)' }}>
                    <div className="w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow" />
                    <div className="absolute inset-0 w-3 h-3 bg-green-400 rounded-full animate-ping opacity-60" />
                  </div>
                  
                  {/* Map label */}
                  <div className="absolute bottom-2 left-2 bg-black/30 text-white/80 text-[10px] font-semibold px-2 py-0.5 rounded-md backdrop-blur-sm">
                    Live Map
                  </div>
                </div>
                
                {/* List panel */}
                <div className="flex-[2] bg-white/5 border-l border-white/10 flex flex-col">
                  <div className="px-3 py-2 border-b border-white/10">
                    <div className="h-2.5 bg-white/30 rounded w-2/3" />
                  </div>
                  <div className="flex-1 overflow-hidden divide-y divide-white/10">
                    {[
                      { color: 'bg-accent', label: 'Photo #1', sub: '2 min ago' },
                      { color: 'bg-white/50', label: 'Photo #2', sub: '8 min ago' },
                      { color: 'bg-white/50', label: 'Photo #3', sub: '15 min ago' },
                      { color: 'bg-white/50', label: 'Photo #4', sub: '22 min ago' },
                    ]?.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2">
                        <div className={`w-6 h-6 rounded-md flex-shrink-0 ${item?.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="h-2 bg-white/30 rounded w-3/4 mb-1" />
                          <div className="h-1.5 bg-white/15 rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile app mockup - perfect gaps & maximized maps */}
            <div className="absolute -bottom-6 -right-4 sm:-right-6 w-28 sm:w-32" style={{ zIndex: 10 }}>
              {/* Phone shell */}
              <div 
                className="relative rounded-[22px] border-2 border-white/40 shadow-2xl overflow-hidden backdrop-blur-sm bg-white/10 w-full flex flex-col" 
                style={{ aspectRatio: '9/16' }}
              >
                {/* Dynamic island / notch */}
                <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
                  <div className="w-8 h-1.5 bg-white/20 rounded-full" />
                </div>
                
                {/* Main container - flex container stretches map exactly down to uniform bottom padding */}
                <div className="flex-1 p-1.5 pb-2 flex flex-col gap-1.5 min-h-0">
                  
                  {/* Title bar */}
                  <div className="flex items-center justify-between px-0.5 flex-shrink-0">
                    <div className="h-1.5 bg-white/50 rounded w-10" />
                    <div className="w-3.5 h-3.5 bg-accent/90 rounded-full" />
                  </div>
                  
                  {/* Search + Filter buttons */}
                  <div className="flex gap-1 px-0.5 flex-shrink-0">
                    <div className="flex-1 h-3 bg-white/20 rounded-md flex items-center px-1 gap-0.5">
                      <div className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0" />
                      <div className="h-1 bg-white/40 rounded w-full" />
                    </div>
                    <div className="h-3 w-5 bg-white/20 rounded-md flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-0.5 bg-white/60 rounded" />
                    </div>
                  </div>
                  
                  {/* Mini map - flex-1 automatically takes up all remaining space above bottom padding */}
                  <div className="relative flex-1 w-full bg-primary/40 rounded-xl overflow-hidden border border-white/20">
                    
                    {/* Grid lines calibrated to scale proportionally to fill height */}
                    <div className="absolute inset-0 opacity-25">
                      {[...Array(6)]?.map((_, i) => (
                        <div key={`h${i}`} className="absolute w-full h-px bg-white/40" style={{ top: `${(i + 1) * 16}%` }} />
                      ))}
                      {[...Array(8)]?.map((_, i) => (
                        <div key={`v${i}`} className="absolute h-full w-px bg-white/40" style={{ left: `${(i + 1) * 12}%` }} />
                      ))}
                    </div>
                    
                    {/* Map pins */}
                    {[
                      { top: '22%', left: '18%', accent: true },
                      { top: '45%', left: '38%', accent: false },
                      { top: '30%', left: '58%', accent: false },
                      { top: '62%', left: '25%', accent: false },
                      { top: '55%', left: '70%', accent: true },
                      { top: '18%', left: '75%', accent: false },
                    ]?.map((pin, i) => (
                      <div
                        key={i}
                        className={`absolute w-2 h-2 rounded-full border border-white shadow-md ${pin?.accent ? 'bg-accent' : 'bg-white/70'}`}
                        style={{ top: pin?.top, left: pin?.left, transform: 'translate(-50%,-50%)' }}
                      />
                    ))}
                    
                    {/* Live location dot */}
                    <div className="absolute" style={{ top: '48%', left: '52%', transform: 'translate(-50%,-50%)' }}>
                      <div className="w-2 h-2 bg-green-400 rounded-full border border-white shadow" />
                      <div className="absolute inset-0 w-2 h-2 bg-green-400 rounded-full animate-ping opacity-60" />
                    </div>
                    
                    {/* FAB - bottom right, padded inside container */}
                    <div className="absolute bottom-2 right-2 w-5 h-5 rounded-full shadow-lg flex items-center justify-center border border-white/50 backdrop-blur-sm bg-white/30">
                      <span className="text-white font-bold leading-none" style={{ fontSize: '10px' }}>+</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Mobile app feature strip */}
      <section className="bg-sm-bg border-y border-blue-100 py-14">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            {/* Left: app intro */}
            <div className="lg:w-72 flex-shrink-0">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                CoLoco Mobile
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight leading-snug">
                The fastest way to add data in the field
              </h2>
              <p className="mt-3 text-gray-500 text-sm leading-relaxed">
                Snap a photo, it's pinned to the map. Works offline. Syncs when you're back online. Your team sees it instantly on the web.
              </p>
              <Link
                href="/get-the-app"
                className="mt-5 inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors duration-150 shadow-sm"
              >
                Get the App
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            {/* Right: feature pills */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {mobileFeatures?.map((f, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-blue-100 shadow-card flex gap-3 items-start">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                    {f?.icon}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 mb-0.5">{f?.label}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{f?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Web platform features - bento */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="mb-12">
          <p className="text-xs font-bold text-sm-muted uppercase tracking-widest mb-2">The Web Companion</p>
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Everything you've mapped, beautifully organised</h2>
          <p className="mt-3 text-gray-500 text-base max-w-xl leading-relaxed">
            Open CoLoco on any browser and see your team's photos appear on the map in realtime. Review, filter, and generate reports - no app required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Large card */}
          <div className="lg:col-span-2 bg-sm-bg rounded-2xl p-8 border border-blue-100 flex flex-col sm:flex-row gap-6 items-start">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary flex-shrink-0">
              {webFeatures?.[0]?.icon}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{webFeatures?.[0]?.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{webFeatures?.[0]?.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {['Realtime updates', 'Live locations', 'Map filters', 'Satellite view', 'Route tracks']?.map(tag => (
                  <span key={tag} className="text-xs font-semibold bg-primary/10 text-primary px-3 py-1 rounded-full">{tag}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Report card - accent */}
          <div className="bg-primary rounded-2xl p-8 text-white flex flex-col justify-between min-h-[200px]">
            <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">One-Click Reports</h3>
              <p className="text-blue-100 text-sm leading-relaxed">Turn your entire mapped collection into a professional PDF or Word report in seconds. Share with clients or stakeholders instantly.</p>
            </div>
          </div>

          {/* Regular cards */}
          {webFeatures?.slice(1)?.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card hover:shadow-card-hover transition-shadow duration-200">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-4">
                {f?.icon}
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1.5">{f?.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{f?.description}</p>
            </div>
          ))}

          {/* Realtime accent card */}
          <div className="bg-accent/10 rounded-2xl p-6 border border-accent/20 flex flex-col justify-between">
            <div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center text-accent-dark mb-4">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-1.5">Realtime Everything</h3>
              <p className="text-gray-500 text-sm leading-relaxed">Photos, locations, and updates flow live between mobile and web. No refresh needed - the map just updates.</p>
            </div>
          </div>
        </div>
      </section>
      {/* Who it's for strip */}
      <section className="bg-sm-bg border-y border-blue-100 py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-sm-muted uppercase tracking-widest mb-5">Works for everyone</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Solo explorers',
              'Field researchers',
              'Construction teams',
              'Environmental surveyors',
              'Event coordinators',
              'Inspection crews',
            ]?.map(label => (
              <span key={label} className="px-4 py-2 rounded-full text-sm font-semibold bg-white border border-blue-100 text-gray-700 shadow-sm">
                {label}
              </span>
            ))}
          </div>
          <p className="text-gray-500 text-sm mt-5 max-w-lg mx-auto">
            Whether you're working alone or coordinating a large team, CoLoco keeps every photo in the right place - on the map, in realtime.
          </p>
        </div>
      </section>
      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-4">
          {user ? 'Welcome back!' : 'Start mapping together'}
        </h2>
        <p className="text-gray-500 text-base mb-8 max-w-md mx-auto">
          {user
            ? 'Head to your collections to see what your team has been mapping.' :'Free to get started. Add photos on mobile, view them on the web - live.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            href={user ? (isMobile ? '/get-the-app' : '/collections') : '/login'}
            className="inline-block bg-primary hover:bg-primary-dark text-white font-bold px-10 py-4 rounded-xl transition-colors duration-150 shadow-lg text-base"
          >
            {user ? 'Go to My Collections' : 'Get Started Free'}
          </Link>
          {!user && (
            <Link
              href="/get-the-app"
              className="inline-block bg-sm-bg hover:bg-blue-100 text-primary font-bold px-10 py-4 rounded-xl transition-colors duration-150 border border-blue-200 text-base"
            >
              Get CoLoco Mobile
            </Link>
          )}
        </div>
      </section>
      {/* Footer */}
      <Footer />
    </div>
  );
}
